import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, TextField, Autocomplete, Alert, CircularProgress,
  Grid, Divider, MenuItem, Table, TableBody, TableCell, TableRow, Chip
} from '@mui/material';
import CalculateIcon from '@mui/icons-material/Calculate';
import PrintIcon from '@mui/icons-material/Print';
import GavelIcon from '@mui/icons-material/Gavel';
import axios from 'axios';

const fmt = n => `S/ ${Number(n || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const fmtDate = v => v ? new Date(v + 'T12:00:00').toLocaleDateString('es-PE') : '—';

const TIPO_CESE_OPTS = [
  { value: 'RENUNCIA', label: 'Renuncia voluntaria' },
  { value: 'MUTUO_ACUERDO', label: 'Mutuo acuerdo' },
  { value: 'DESPIDO_ARBITRARIO', label: 'Despido arbitrario (con indemnización)' },
  { value: 'VENCIMIENTO_CONTRATO', label: 'Vencimiento de contrato' },
  { value: 'FALLECIMIENTO', label: 'Fallecimiento' },
  { value: 'JUBILACION', label: 'Jubilación' },
];

export default function LiquidacionRRHH() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [trabajadores, setTrabajadores] = useState([]);
  const [trabajadorSel, setTrabajadorSel] = useState(null);
  const [fechaCese, setFechaCese] = useState(new Date().toLocaleDateString('en-CA'));
  const [tipoCese, setTipoCese] = useState('RENUNCIA');
  const [resultado, setResultado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get('/api/rrhh/empresas').then(r => {
      setEmpresas(Array.isArray(r.data) ? r.data : []);
      if (r.data?.length === 1) setEmpresaId(r.data[0].empresa_id);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!empresaId) return;
    axios.get(`/api/rrhh/trabajadores?empresa_id=${empresaId}`).then(r => {
      setTrabajadores(Array.isArray(r.data) ? r.data : []);
    }).catch(() => {});
    setTrabajadorSel(null);
    setResultado(null);
  }, [empresaId]);

  const handleCalcular = async () => {
    if (!trabajadorSel || !fechaCese) return setError('Seleccione trabajador y fecha de cese');
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const r = await axios.get(`/api/rrhh/trabajadores/${trabajadorSel.id}/liquidacion?fecha_cese=${fechaCese}&tipo_cese=${tipoCese}`);
      setResultado(r.data);
    } catch (e) { setError(e.response?.data?.error || 'Error al calcular'); }
    finally { setLoading(false); }
  };

  const handleImprimir = () => {
    if (!resultado) return;
    const win = window.open('', '_blank');
    const trab = resultado.trabajador;
    const det = resultado.detalle;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Liquidación</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:11px;color:#222;margin:24px}
      h2{color:#7b1fa2;margin:0}h3{color:#555;margin:4px 0 12px}
      .header{border-bottom:3px solid #7b1fa2;padding-bottom:8px;margin-bottom:16px}
      table{width:100%;border-collapse:collapse;margin-bottom:12px}
      th{background:#7b1fa2;color:white;padding:5px 8px;text-align:left}
      td{padding:4px 8px;border-bottom:1px solid #eee}
      .total{background:#f3e5f5;font-weight:bold;font-size:13px}
      .row-alt{background:#fafafa}
      .chip{display:inline-block;padding:2px 8px;border-radius:12px;font-size:10px}
    </style></head><body>
    <div class="header"><h2>LIQUIDACIÓN DE BENEFICIOS SOCIALES</h2></div>
    <table>
      <tr><th colspan="4">DATOS DEL TRABAJADOR</th></tr>
      <tr><td><b>Trabajador:</b> ${trab.nombre}</td><td><b>Doc.:</b> ${trab.num_doc}</td>
          <td><b>Ingreso:</b> ${fmtDate(trab.fecha_ingreso)}</td><td><b>Cese:</b> ${fmtDate(trab.fecha_cese)}</td></tr>
      <tr><td><b>Tiempo servicio:</b> ${trab.anios_servicio} años</td>
          <td><b>Rem. Básica:</b> ${fmt(trab.remuneracion_basica)}</td>
          <td><b>Asig. Familiar:</b> ${fmt(trab.asignacion_familiar)}</td>
          <td><b>Rem. Computable:</b> ${fmt(trab.rem_computable)}</td></tr>
      <tr><td colspan="4"><b>Tipo de cese:</b> ${TIPO_CESE_OPTS.find(t=>t.value===det.tipo_cese)?.label||det.tipo_cese}</td></tr>
    </table>
    <table>
      <tr><th>CONCEPTO</th><th>DETALLE</th><th style="text-align:right">IMPORTE</th></tr>
      <tr><td>CTS Proporcional</td><td>${det.cts_meses} mes(es)</td><td align="right">${fmt(det.cts_proporcional)}</td></tr>
      <tr class="row-alt"><td>Gratificación Proporcional</td><td>${det.gratif_meses} mes(es)</td><td align="right">${fmt(det.gratif_proporcional)}</td></tr>
      <tr><td>Bonif. Extraordinaria (9% Gratif.)</td><td>EsSalud sobre gratif.</td><td align="right">${fmt(det.bonif_extraordinaria)}</td></tr>
      <tr class="row-alt"><td>Vacaciones Pendientes</td><td>${det.vacaciones_pendientes_dias} día(s)</td><td align="right">${fmt(det.vacaciones_pendientes_importe)}</td></tr>
      <tr><td>Vacaciones Truncas</td><td>Año en curso proporcional</td><td align="right">${fmt(det.vacaciones_truncas)}</td></tr>
      ${det.indemnizacion > 0 ? `<tr class="row-alt"><td>Indemnización por Despido</td><td>Según ley laboral</td><td align="right">${fmt(det.indemnizacion)}</td></tr>` : ''}
      <tr class="total"><td colspan="2">TOTAL LIQUIDACIÓN</td><td align="right">${fmt(resultado.total_liquidacion)}</td></tr>
    </table>
    <p style="font-size:9px;color:#888;margin-top:24px">Generado por InsightSoft ERP — ${new Date().toLocaleString('es-PE')}</p>
    <div style="display:flex;justify-content:space-between;margin-top:48px">
      <div style="text-align:center"><div style="border-top:1px solid #999;width:180px;padding-top:4px">Firma Empleador</div></div>
      <div style="text-align:center"><div style="border-top:1px solid #999;width:180px;padding-top:4px">Firma Trabajador</div></div>
    </div>
    </body></html>`;
    win.document.write(html);
    win.document.close();
    win.print();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 1, mb: 4, width: '100%' }}>
      <Box sx={{ width: { xs: '100%', sm: '95%', md: '85%' }, maxWidth: 1000 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <GavelIcon sx={{ color: '#7b1fa2', fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#222' }}>Liquidación al Cese</Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold" color="#7b1fa2" sx={{ mb: 2 }}>Datos del Cese</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={empresas} getOptionLabel={o => o.razon_social || ''}
                value={empresas.find(e => e.empresa_id === empresaId) || null}
                onChange={(_, v) => setEmpresaId(v?.empresa_id || '')}
                isOptionEqualToValue={(o, v) => o.empresa_id === v.empresa_id}
                renderInput={p => <TextField {...p} label="Empresa" size="small" sx={{ minWidth: '16em' }} />}
                sx={{ minWidth: '16em' }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                options={trabajadores}
                getOptionLabel={o => `${o.apellido_paterno} ${o.apellido_materno}, ${o.nombres}` || ''}
                value={trabajadorSel}
                onChange={(_, v) => setTrabajadorSel(v)}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                renderInput={p => <TextField {...p} label="Trabajador" size="small" sx={{ minWidth: '16em' }} />}
                sx={{ minWidth: '16em' }}
                disabled={!empresaId}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Fecha de Cese" type="date" size="small" value={fechaCese}
                onChange={e => setFechaCese(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ minWidth: '16em' }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth select label="Tipo de Cese" size="small" value={tipoCese}
                onChange={e => setTipoCese(e.target.value)} sx={{ minWidth: '16em' }}>
                {TIPO_CESE_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <CalculateIcon />}
                onClick={handleCalcular} disabled={loading || !trabajadorSel || !fechaCese}
                sx={{ bgcolor: '#7b1fa2', minWidth: '16em' }}>
                {loading ? 'Calculando...' : 'Calcular Liquidación'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {resultado && (
          <>
            <Paper sx={{ p: 3, borderRadius: 4, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" fontWeight="bold">{resultado.trabajador.nombre}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Doc: {resultado.trabajador.num_doc} | Ingreso: {fmtDate(resultado.trabajador.fecha_ingreso)} |
                    Cese: {fmtDate(resultado.trabajador.fecha_cese)} | Tiempo: {resultado.trabajador.anios_servicio} años
                  </Typography>
                  <Chip size="small" label={TIPO_CESE_OPTS.find(t => t.value === tipoCese)?.label} sx={{ mt: 0.5, bgcolor: '#f3e5f5', color: '#7b1fa2' }} />
                </Box>
                <Button variant="outlined" startIcon={<PrintIcon />} onClick={handleImprimir} sx={{ borderColor: '#7b1fa2', color: '#7b1fa2' }}>
                  Imprimir
                </Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                {[
                  ['Rem. Básica', resultado.trabajador.remuneracion_basica],
                  ['Asig. Familiar', resultado.trabajador.asignacion_familiar],
                  ['Rem. Computable', resultado.trabajador.rem_computable],
                ].map(([label, val]) => (
                  <Paper key={label} elevation={0} sx={{ p: 1.5, bgcolor: '#f3e5f5', borderRadius: 2, flex: 1, minWidth: 130, textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">{label}</Typography>
                    <Typography variant="body1" fontWeight="bold" color="#7b1fa2">{fmt(val)}</Typography>
                  </Paper>
                ))}
              </Box>

              <Divider sx={{ my: 2 }} />

              <Table size="small">
                <TableBody>
                  {[
                    ['CTS Proporcional', `${resultado.detalle.cts_meses} mes(es)`, resultado.detalle.cts_proporcional, '#1976d2'],
                    ['Gratificación Proporcional', `${resultado.detalle.gratif_meses} mes(es)`, resultado.detalle.gratif_proporcional, '#1976d2'],
                    ['Bonif. Extraordinaria (9% Gratif.)', 'Equivalente a EsSalud', resultado.detalle.bonif_extraordinaria, '#1976d2'],
                    ['Vacaciones Pendientes', `${resultado.detalle.vacaciones_pendientes_dias} día(s) sin gozar`, resultado.detalle.vacaciones_pendientes_importe, '#2e7d32'],
                    ['Vacaciones Truncas', 'Año en curso proporcional', resultado.detalle.vacaciones_truncas, '#2e7d32'],
                    resultado.detalle.indemnizacion > 0 ? ['Indemnización por Despido Arbitrario', 'Según D.Leg. 728', resultado.detalle.indemnizacion, '#c62828'] : null,
                  ].filter(Boolean).map(([concepto, detalle, importe, color], i) => (
                    <TableRow key={i} sx={{ bgcolor: i % 2 === 0 ? '#fafafa' : 'white' }}>
                      <TableCell sx={{ fontWeight: 'medium' }}>{concepto}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', fontSize: '0.8em' }}>{detalle}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color, minWidth: 110 }}>{fmt(importe)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ mt: 2, p: 2, bgcolor: '#7b1fa2', borderRadius: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight="bold" color="white">TOTAL LIQUIDACIÓN</Typography>
                <Typography variant="h4" fontWeight="bold" color="white">{fmt(resultado.total_liquidacion)}</Typography>
              </Box>
            </Paper>

            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Nota:</strong> Los montos son referenciales y están calculados en base a los datos registrados en el sistema.
              La liquidación definitiva debe ser validada con los documentos originales (boletas, contratos, historial CTS y gratificaciones).
            </Alert>
          </>
        )}
      </Box>
    </Box>
  );
}
